/**
 * @fileoverview Module for `src/admin/Announcements.js`.
 */

import React, { Fragment } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  Filter,
  FormDataConsumer,
  TextInput,
  BulkExportButton,
  SelectInput,
  Create,
  SimpleForm,
  Toolbar,
  required,
  SaveButton,
  Edit,
  DateInput,
  DateTimeInput,
  EditButton,
  useNotify,
} from 'react-admin';
import { createTimezoneDate } from '../lib/dates';

/**
 * Edit toolbar for announcement records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Toolbar with save action.
 */
const AnnouncementEditToolbar = (props) => (
//removes default delete option from toolbar
  <Toolbar { ...props } >
    <SaveButton />
  </Toolbar>
);

/**
 * Lightbox-specific announcement form inputs.
 *
 * @returns {JSX.Element} Lightbox form controls.
 */
const LightboxInputs = () => (
  <div>
    <TextInput helperText='Nav Bar Text to Trigger Lightbox' source='navBarText' src='url' /><br />
    <TextInput helperText='Header' source='header' src='url' /><br />
    <TextInput helperText='Secondary header' source='secondaryHeader' src='url' /><br />
    <TextInput fullWidth helperText='Secondary content; only a i p and strong tags allowed' multiline source='secondaryContent' src='url' /><br />
  </div>
);

/**
 * Event-specific announcement form inputs.
 *
 * @returns {JSX.Element} Event form controls.
 */
const EventInputs = () => (
  <Fragment>
    <TextInput helperText='URL of image' source='imgSrc' src='url' validate={ [required()] } /><br />
    <TextInput helperText='URL where the button goes' source='buttonHref' src='url' validate={ [required()] } /><br />
    <DateTimeInput source='eventStartAt' validate={ [required()] } /><br />
  </Fragment>
);

/**
 * Non-event announcement form inputs.
 *
 * @returns {JSX.Element} Banner and lightbox CTA inputs.
 */
const NonEventInputs = () => (
  <Fragment>
    <TextInput fullWidth helperText="Call-to-action text, like 'click here'; only a i and strong tags allowed" source='ctaText' /><br />
    <TextInput helperText='Call-to-action URL' source='ctaUrl' src='url' /><br />
  </Fragment>
);

/**
 * Shared announcement inputs used in create and edit flows.
 *
 * @returns {JSX.Element} Shared announcement fields.
 */
const SharedInputs = () => (
  <Fragment>
    <SelectInput
      choices={ [
        { id: 'BANNER', name: 'Banner' },
        { id: 'LIGHTBOX', name: 'Lightbox' },
        { id: 'EVENT', name: 'Event' },
      ] }
      source='type'
      validate={ [required()] }
    />
    <br />
    <TextInput fullWidth helperText='For events this is the event name. Only a i p and strong tags allowed.' multiline source='content' validate={ [required()] } /><br />
    <FormDataConsumer>
      { ({ formData, ...rest }) => (
        <Fragment>

          { formData.type === 'LIGHTBOX' && <LightboxInputs { ...rest } /> }
          { ['LIGHTBOX', 'BANNER'].includes(formData.type) && <NonEventInputs /> }
          { formData.type === 'EVENT' && <EventInputs /> }
          <br />
        </Fragment>
      ) }
    </FormDataConsumer>
    <DateInput format={ (v) => (v ? createTimezoneDate(v).toISOString().split('T')[0] : v) } source='activeFrom' validate={ [required()] } /><br />
    <DateInput format={ (v) => (v ? createTimezoneDate(v).toISOString().split('T')[0] : v) } source='activeTo' validate={ [required()] } /><br />
  </Fragment>
);


/**
 * Maps announcement API failures to admin notifications.
 *
 * @param {Object} error
 * @param {Function} notify
 * @returns {void}
 */
const errorHandler = (error, notify) => {
  notify(`Error: ${ error.body || error.message }`);
};

/**
 * Edit view for announcement records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Announcement editor.
 */
const AnnouncementEdit = (props) => {
  const notify = useNotify();
  /**
   * Delegates announcement edit errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };

  return (
    <Edit undoable={ false } { ...props } onFailure={ onFailure }>
      <SimpleForm redirect='edit' toolbar={ <AnnouncementEditToolbar /> }>
        <TextInput disabled source='id' />
        <SharedInputs />
      </SimpleForm>
    </Edit>
  );
};

/**
 * Create toolbar for announcement records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Toolbar with create action.
 */
const AnnouncementCreateToolbar = (props) => (
  <Toolbar { ...props } >
    <SaveButton
      label='create'
    />
  </Toolbar>
);

/**
 * Create view for announcement records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Announcement creation form.
 */
const AnnouncementCreate = (props) => {
  const notify = useNotify();
  /**
   * Delegates announcement creation errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };
  return (
    <Create { ...props } onFailure={ onFailure }>
      <SimpleForm toolbar={ <AnnouncementCreateToolbar /> }>
        <SharedInputs />
      </SimpleForm>
    </Create>
  );
};

/**
 * Bulk action controls for announcement lists.
 *
 * @param {Object} props
 * @returns {JSX.Element} Bulk action buttons.
 */
const AnnouncementListBulkActionButtons = (props) => (
  <BulkExportButton { ...props } />
);

/**
 * Filter controls for announcements.
 *
 * @param {Object} props
 * @returns {JSX.Element} Announcement filters.
 */
const AnnouncementFilter = (props) => (
  <Filter { ...props }>
    <DateInput label='Active From <=' source='activeFrom:lessThanOrEqualTo' />
    <DateInput label='Active To >=' source='activeTo:greaterThanOrEqualTo' />
  </Filter>
);

/**
 * List view for announcement records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Announcement data grid.
 */
const AnnouncementList = (props) => (
  <List { ...props } bulkActionButtons={ <AnnouncementListBulkActionButtons /> } filters={ <AnnouncementFilter /> } sort={{ field: 'id', order: 'DESC' }}>
    <Datagrid>
      <TextField source='id' />
      <TextField source='type' />
      { /*<ImageField source="logo" />*/ }
      <TextField source='content' />
      <FunctionField
        label='Cta'
        render={ (record) => (
          <a href={ record.ctaUrl } rel='noreferrer' target='_blank'>
            { record.ctaText }
          </a>
        ) }
        sortBy='ctaText'
      />
      <FunctionField render={ ({ activeFrom }) => (activeFrom ? createTimezoneDate(activeFrom).toISOString().split('T')[0] : activeFrom) } source='activeFrom' />
      <FunctionField render={ ({ activeTo }) => (activeTo ? createTimezoneDate(activeTo).toISOString().split('T')[0] : activeTo) } source='activeTo' />
      <EditButton { ...props } />
    </Datagrid>
  </List>
);

export default AnnouncementList;
export { AnnouncementCreate, AnnouncementEdit };
